import { PLAN_OPERATION_CREATE } from '@/constants/ui.constants';
import type {
  Artifact,
  Clarification,
  ClarificationQuestion,
  Plan,
} from '@/types/artifacts.types';
import type { ToolCallContent } from '@/types/messages';
import {
  ASK_USER_EXPIRED_RESULT,
  ASK_USER_SKIPPED_RESULT,
  parseAskUserArgs,
  parsePlanResult,
  type ClarificationAnswer,
} from '@/types/tools';

/**
 * The legacy reader: the artifact of a block saved without one, by its tool
 * name, args and result. It reads blocks saved before artifacts, and those of
 * a reasoner older than the UI. The one place the UI checks a tool name.
 */

/** The tools whose blocks the legacy reader draws. */
export const SPECIAL_TOOL_NAMES = {
  PLAN: 'plan',
  ASK_USER: 'ask_user',
} as const;

const CLARIFICATION_ANSWER_ARROW = ' → ';
const CLARIFICATION_LABEL_SEPARATOR = ', ';
const CLARIFICATION_OTHER_PREFIX = 'Other: ';

/** The start of a question's reply line; a line break would split it. */
function clarificationLinePrefix(question: ClarificationQuestion): string {
  return `${question.question.replace(/\s*\n\s*/g, ' ')}${CLARIFICATION_ANSWER_ARROW}`;
}

/**
 * The reply text of a clarification card, which the model reads: one line per
 * question, in question order, that still reads on its own if the question is
 * compacted out of the history. Labels keep option order; a typed Other text
 * comes last, after `Other: `.
 */
export function formatClarificationAnswer(
  questions: ClarificationQuestion[],
  answers: ClarificationAnswer[],
): string {
  return questions
    .map((question, questionIndex) => {
      const { picks, other } = answers[questionIndex];
      const parts = question.options
        .filter((_, optionIndex) => picks.includes(optionIndex))
        .map((option) => option.label);
      if (other) parts.push(`${CLARIFICATION_OTHER_PREFIX}${other}`);
      return `${clarificationLinePrefix(question)}${parts.join(CLARIFICATION_LABEL_SEPARATOR)}`;
    })
    .join('\n');
}

/** The answer of a reply that is only Other text, else null. */
function matchOther(reply: string): ClarificationAnswer | null {
  return reply.startsWith(CLARIFICATION_OTHER_PREFIX)
    ? { picks: [], other: reply.slice(CLARIFICATION_OTHER_PREFIX.length) }
    : null;
}

/**
 * A multi-select reply from option `from` on: labels in option order, then
 * optional Other text. Backtracks, as a label may hold the separator; null
 * unless the whole reply matches.
 */
function matchLabels(
  options: ClarificationQuestion['options'],
  reply: string,
  from: number,
): ClarificationAnswer | null {
  const other = matchOther(reply);
  if (other) return other;
  for (let optionIndex = from; optionIndex < options.length; optionIndex++) {
    const { label } = options[optionIndex];
    if (reply === label) return { picks: [optionIndex], other: '' };
    const prefix = `${label}${CLARIFICATION_LABEL_SEPARATOR}`;
    if (reply.startsWith(prefix)) {
      const rest = matchLabels(
        options,
        reply.slice(prefix.length),
        optionIndex + 1,
      );
      if (rest) {
        return { picks: [optionIndex, ...rest.picks], other: rest.other };
      }
    }
  }
  return null;
}

/** A single-select reply: one label or only Other text, else null. */
function matchLabel(
  options: ClarificationQuestion['options'],
  reply: string,
): ClarificationAnswer | null {
  const other = matchOther(reply);
  if (other) return other;
  const optionIndex = options.findIndex(({ label }) => label === reply);
  return optionIndex === -1 ? null : { picks: [optionIndex], other: '' };
}

/**
 * The answers a `formatClarificationAnswer` reply holds: line i answers
 * question i. A question whose line does not match, e.g. of a reply typed in
 * the composer, gets an empty answer.
 */
export function parseClarificationAnswer(
  questions: ClarificationQuestion[],
  text: string,
): ClarificationAnswer[] {
  const lines = text.split('\n');
  return questions.map((question, questionIndex) => {
    const prefix = clarificationLinePrefix(question);
    const line: string | undefined = lines[questionIndex];
    if (line === undefined || !line.startsWith(prefix)) {
      return { picks: [], other: '' };
    }
    const reply = line.slice(prefix.length);
    const answer = question.multi_select
      ? matchLabels(question.options, reply, 0)
      : matchLabel(question.options, reply);
    return answer ?? { picks: [], other: '' };
  });
}

/** How an ask_user call closed. */
export type AskUserOutcome =
  | { kind: 'answer'; text: string }
  | { kind: 'skipped' }
  | { kind: 'expired' };

/** The outcome an ask_user call's result holds; null while it waits. */
export function getAskUserOutcome(
  toolCall: ToolCallContent,
): AskUserOutcome | null {
  const { result } = toolCall;
  if (toolCall.isError || typeof result !== 'string') return null;
  if (result === ASK_USER_SKIPPED_RESULT) return { kind: 'skipped' };
  if (result === ASK_USER_EXPIRED_RESULT) return { kind: 'expired' };
  return { kind: 'answer', text: result };
}

function legacyClarification(call: ToolCallContent): Clarification {
  const questions = parseAskUserArgs(call.args);
  const clarification = (
    outcome: Clarification['outcome'],
  ): Clarification => ({
    kind: 'clarification',
    version: 1,
    questions: questions ?? [],
    outcome,
  });
  if (!questions) {
    return clarification({ state: 'rejected', text: null });
  }
  const outcome = getAskUserOutcome(call);
  if (outcome === null) {
    return clarification({ state: 'waiting', text: null });
  }
  if (outcome.kind !== 'answer') {
    return clarification({ state: outcome.kind, text: null });
  }
  return clarification({ state: 'answered', text: outcome.text });
}

function legacyPlan(call: ToolCallContent): Plan {
  const parsed = parsePlanResult(call.result);
  const args = call.args;
  return {
    kind: 'plan',
    version: 1,
    plan_id:
      parsed?.plan_id ||
      (args?.plan_id as string | undefined) ||
      (args?.planId as string | undefined) ||
      null,
    operation: ((args?.action as string | undefined) ||
      (args?.operation as string | undefined) ||
      PLAN_OPERATION_CREATE) as Plan['operation'],
    ok: parsed ? parsed.success : null,
    message: parsed ? parsed.message : null,
    plan_text:
      parsed?.planText || (args?.planText as string | undefined) || null,
    todos: parsed ? (parsed.todos ?? []) : null,
  };
}

/** The artifact of a block saved without one; null for any other tool. */
export function legacyArtifact(call: ToolCallContent): Artifact | null {
  switch (call.toolName) {
    case SPECIAL_TOOL_NAMES.ASK_USER:
      return legacyClarification(call);
    case SPECIAL_TOOL_NAMES.PLAN:
      return legacyPlan(call);
    default:
      return null;
  }
}

/** Unwrap API result format { type: 'text', text: '...' } to get raw result for plan parsing */
function unwrapToolResult(result: unknown): unknown {
  if (!result || typeof result !== 'object') return result;
  const obj = result as Record<string, unknown>;

  // Handle { type: 'text', text: '...' } (possibly nested)
  if (obj.type === 'text' && typeof obj.text === 'string') {
    const text = obj.text;
    try {
      const parsed = JSON.parse(text);
      // Handle array-wrapped responses (e.g. "[{...}]")
      return Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : parsed;
    } catch {
      return text;
    }
  }

  // Handle nested result (e.g. result.result with inner { type: 'text', text })
  const inner = obj.result ?? obj.data ?? obj.output;
  if (inner) return unwrapToolResult(inner);

  return result;
}

/** Check if a string looks like plan markdown (for fallback when parsePlanResult fails) */
function looksLikePlanMarkdown(s: string): boolean {
  const t = s.trim();
  return (
    t.length > 0 &&
    (t.includes('##') ||
      t.includes('###') ||
      /Plan|Overview|Investigation|Deliverables/i.test(t))
  );
}

/**
 * The plan text copying a block saved without an artifact reads, as copy read
 * it before artifacts; null for a block of any other tool.
 */
export function legacyPlanText(call: ToolCallContent): string | null {
  if (call.toolName !== SPECIAL_TOOL_NAMES.PLAN) return null;
  const raw = unwrapToolResult(call.result);
  const parsed = parsePlanResult(raw);
  const fromParsed =
    parsed?.planText || (call.args?.planText as string | undefined) || null;
  if (fromParsed) return fromParsed;
  // Fallback: when result is raw markdown (e.g. plan sent as plain text)
  if (typeof raw === 'string' && looksLikePlanMarkdown(raw)) {
    return raw.trim();
  }
  return null;
}
